/**
 * +world-resources: pooled shatter chunks (one InstancedMesh, one draw call). Chunks fly on
 * ballistic arcs, spin, bounce on the floor with friction, settle, then shrink away. Fixed
 * capacity; nothing allocates after construction (spawn evicts the oldest when full).
 */
import * as THREE from 'three';
import { groundY } from '../../ground';
import { ownedToonMaterial } from '../../style';

const GRAVITY = 20;
const SETTLE_S = 0.9;
const FADE_S = 0.45;

export interface ShardInit {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  size: number;
  /** Linear RGB. */
  r: number;
  g: number;
  b: number;
  /** Extra seconds on the floor before fading. */
  linger: number;
}

export function blankShard(): ShardInit {
  return { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, size: 0.15, r: 1, g: 1, b: 1, linger: 0 };
}

export class ShardPool {
  readonly mesh: THREE.InstancedMesh;
  readonly capacity: number;
  private readonly geo: THREE.BufferGeometry;
  private readonly mat: THREE.MeshToonMaterial;
  /** Per chunk: x y z vx vy vz | ax ay az (axis) angle spin | age life size floor. */
  private readonly p: Float32Array;
  private readonly alive: Uint8Array;
  private next = 0;
  private hwm = 0;
  live = 0;
  private readonly m4 = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly v = new THREE.Vector3();
  private readonly s = new THREE.Vector3();
  private readonly ax = new THREE.Vector3();
  private readonly c = new THREE.Color();
  private static readonly STRIDE = 15;

  constructor(capacity = 220) {
    this.capacity = capacity;
    // A chunky wedge (flattened, irregular octahedron) reads as a pottery / wood shard.
    const g = new THREE.OctahedronGeometry(0.5, 0);
    g.scale(1.0, 0.55, 0.75);
    const pa = g.attributes['position'] as THREE.BufferAttribute;
    for (let i = 0; i < pa.count; i++) if (pa.getX(i) > 0.3) pa.setX(i, pa.getX(i) * 0.6);
    g.computeVertexNormals();
    this.geo = g;
    this.mat = ownedToonMaterial({ color: 0xffffff });
    this.mat.name = 'lootShards';
    this.mesh = new THREE.InstancedMesh(this.geo, this.mat, capacity);
    this.mesh.name = 'lootShards';
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.setColorAt(0, this.c.setRGB(1, 1, 1));
    this.mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.userData['noStyle'] = true;
    this.p = new Float32Array(capacity * ShardPool.STRIDE);
    this.alive = new Uint8Array(capacity);
  }

  spawn(s: ShardInit): void {
    // Round-robin: the oldest slot is reused when full (chunks are short-lived anyway).
    let i = -1;
    for (let k = 0; k < this.capacity; k++) {
      const j = (this.next + k) % this.capacity;
      if (!this.alive[j]) {
        i = j;
        break;
      }
    }
    if (i < 0) i = this.next;
    this.next = (i + 1) % this.capacity;
    if (!this.alive[i]) this.live++;
    this.alive[i] = 1;
    if (i + 1 > this.hwm) this.hwm = i + 1;
    const o = i * ShardPool.STRIDE;
    const p = this.p;
    p[o] = s.x;
    p[o + 1] = s.y;
    p[o + 2] = s.z;
    p[o + 3] = s.vx;
    p[o + 4] = s.vy;
    p[o + 5] = s.vz;
    // Random-ish spin axis from the velocity (deterministic, no Math.random needed).
    const ax = s.vz + 0.3;
    const ay = s.vx * 0.5 + 0.7;
    const az = -s.vx + 0.2;
    const l = 1 / (Math.hypot(ax, ay, az) || 1);
    p[o + 6] = ax * l;
    p[o + 7] = ay * l;
    p[o + 8] = az * l;
    p[o + 9] = (s.x * 13.7 + s.z * 7.1) % 6.28;
    p[o + 10] = 8 + Math.hypot(s.vx, s.vz) * 3;
    p[o + 11] = 0;
    p[o + 12] = SETTLE_S + s.linger + FADE_S;
    p[o + 13] = s.size;
    p[o + 14] = groundY(s.x, -s.z);
    this.mesh.setColorAt(i, this.c.setRGB(s.r, s.g, s.b));
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  update(dt: number): void {
    const p = this.p;
    let top = 0;
    for (let i = 0; i < this.hwm; i++) {
      if (!this.alive[i]) continue;
      const o = i * ShardPool.STRIDE;
      const age = p[o + 11]! + dt;
      const life = p[o + 12]!;
      if (age >= life) {
        this.alive[i] = 0;
        this.live--;
        this.m4.makeScale(0, 0, 0);
        this.mesh.setMatrixAt(i, this.m4);
        continue;
      }
      p[o + 11] = age;
      const floor = p[o + 14]!;
      let y = p[o + 1]!;
      let vy = p[o + 4]!;
      const onFloor = y <= floor + 0.04 && Math.abs(vy) < 0.6;
      if (!onFloor) {
        vy -= GRAVITY * dt;
        p[o] = p[o]! + p[o + 3]! * dt;
        p[o + 2] = p[o + 2]! + p[o + 5]! * dt;
        y += vy * dt;
        p[o + 9] = p[o + 9]! + p[o + 10]! * dt;
        if (y < floor + 0.04 && vy < 0) {
          y = floor + 0.04;
          vy = -vy * 0.32;
          p[o + 3] = p[o + 3]! * 0.5;
          p[o + 5] = p[o + 5]! * 0.5;
          p[o + 10] = p[o + 10]! * 0.5;
        }
      } else {
        y = floor + 0.04;
        vy = 0;
      }
      p[o + 1] = y;
      p[o + 4] = vy;
      const fadeIn = life - FADE_S;
      const k = age > fadeIn ? 1 - (age - fadeIn) / FADE_S : 1;
      const sz = p[o + 13]! * k * Math.min(1, age * 30 + 0.3);
      this.ax.set(p[o + 6]!, p[o + 7]!, p[o + 8]!);
      this.q.setFromAxisAngle(this.ax, p[o + 9]!);
      this.m4.compose(this.v.set(p[o]!, y, p[o + 2]!), this.q, this.s.set(sz, sz, sz));
      this.mesh.setMatrixAt(i, this.m4);
      top = i + 1;
    }
    this.hwm = top;
    this.mesh.count = this.hwm;
    if (this.hwm > 0) this.mesh.instanceMatrix.needsUpdate = true;
  }

  clear(): void {
    this.alive.fill(0);
    this.live = 0;
    this.hwm = 0;
    this.mesh.count = 0;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.dispose();
    this.geo.dispose();
    this.mat.dispose();
  }
}
